import {
  AHText,
  AHTextFonts,
  AHWidgetTitle,
  Box,
  TouchableBox,
  useScaleFactor,
} from "arkh";
import { DateTime } from "luxon";
import React, { useMemo } from "react";
import { View } from "react-native";

import { WidgetWeatherBackground } from "./WidgetWeatherBackground";
import { IconComponent } from "./WidgetWeatherConditionIcon";
import { graphql, useFragment } from "react-relay";
import { WidgetWeatherDatePaneListItem_WeatherOccasion$key } from "./__generated__/WidgetWeatherDatePaneListItem_WeatherOccasion.graphql";

import {
  WidgetWeatherSize,
  WidgetWeather_widget$key,
} from "./__generated__/WidgetWeather_widget.graphql";
import { WidgetWeatherCurrentPane_weatherOccasion$key } from "./__generated__/WidgetWeatherCurrentPane_weatherOccasion.graphql";
import { useSetWidgetFocus } from "../useSetFocus";

const textShadow = {
  textShadowOffset: { width: 0, height: 0 },
  textShadowRadius: 4,
  textShadowColor: "rgba(0, 0, 0, 0.15)",
};

const WidgetWeatherComponent = (props: {
  widget: WidgetWeather_widget$key;
  storecastId?: string;
  clientForceSize?: WidgetWeatherSize;
  mode?: "FOCUS" | "IDLE";
}) => {
  const today = DateTime.local().startOf("day");
  const tomorrow = DateTime.local().plus({ day: 1 });

  const widget = useFragment(
    graphql`
      fragment WidgetWeather_widget on WidgetWeather {
        id
        title
        size
        ...WidgetWeatherBackground_WidgetWeather
        currentOccasion {
          ...WidgetWeatherCurrentPane_weatherOccasion
        }
        occasions {
          date
          ...WidgetWeatherDatePaneListItem_WeatherOccasion
        }
      }
    `,
    props.widget
  );
  const size = props.clientForceSize ?? widget.size;
  const focus = useSetWidgetFocus(widget.id, props.storecastId);
  const todaysOccasion = useMemo(
    () =>
      widget.occasions.find((occasion) => {
        const date = DateTime.fromISO(occasion.date);
        return date.ordinal === today.ordinal && date.year === today.year;
      }),
    [today.ordinal, today.year, widget.occasions]
  );

  const nextOccasion =
    size === "ONE_X_ONE"
      ? widget.occasions.find((occasion) => {
          const date = DateTime.fromISO(occasion.date);
          return (
            date.ordinal === tomorrow.ordinal && date.year === tomorrow.year
          );
        })
      : undefined;

  return (
    <TouchableBox
      color="surface"
      disabled={props.mode !== "FOCUS"}
      onPress={() => focus()}
      style={{
        flex: 1,
        flexDirection: "row",
        position: "relative",
      }}
      spaceVertical="medium"
    >
      <WidgetWeatherBackground widget={widget} />
      <View style={{ position: "absolute", top: 0, left: 0 }}>
        <AHWidgetTitle color="lightText">{widget.title}</AHWidgetTitle>
      </View>
      {widget.currentOccasion ? (
        <CurrentPane
          occasion={widget.currentOccasion}
          nextOccasion={nextOccasion}
          todaysOccasion={todaysOccasion}
        />
      ) : null}
      {size === "TWO_X_ONE" ? (
        <Box spaceHorizontal="small" style={{ flex: 1, flexBasis: "50%" }}>
          {widget.occasions
            .filter((occasion) => occasion.date !== todaysOccasion?.date)
            .map((occasion) => (
              <DatePaneListItem key={occasion.date} occasion={occasion} />
            ))}
        </Box>
      ) : null}
    </TouchableBox>
  );
};

function CurrentPane(props: {
  occasion: WidgetWeatherCurrentPane_weatherOccasion$key;
  todaysOccasion?: WidgetWeatherDatePaneListItem_WeatherOccasion$key;
  nextOccasion?: WidgetWeatherDatePaneListItem_WeatherOccasion$key;
}) {
  const sf = useScaleFactor();
  const currentOccasion = useFragment(
    graphql`
      fragment WidgetWeatherCurrentPane_weatherOccasion on WidgetWeatherOccasion {
        temp
        description
      }
    `,
    props.occasion
  );
  return (
    <Box
      spaceLeft="small"
      style={{
        flex: 1,
        flexBasis: "50%",
        justifyContent: "space-between",
      }}
    >
      <Box spaceTop="medium" style={{ flex: 1, justifyContent: "center" }}>
        <AHText
          color="lightText"
          font={AHTextFonts.BOLDER}
          size={50}
          style={{
            lineHeight: 50 * sf,
            textAlign: "center",
            ...textShadow,
          }}
          type="stat-number"
        >
          {currentOccasion.temp}
          <AHText
            font={AHTextFonts.BOLDER}
            size={50}
            style={{
              lineHeight: 50 * sf,
              position: "absolute",
            }}
            type="stat-number"
            color="lightText"
          >
            °
          </AHText>
        </AHText>
        <AHText
          color="lightText"
          style={{ textAlign: "center", ...textShadow }}
          type="title"
        >
          {currentOccasion.description}
        </AHText>
      </Box>
      {props.todaysOccasion ? (
        <DatePaneListItem
          relativeLabel
          isCompact={!!props.nextOccasion}
          occasion={props.todaysOccasion}
        />
      ) : null}
      {props.nextOccasion ? (
        <DatePaneListItem
          relativeLabel
          isCompact
          occasion={props.nextOccasion}
        />
      ) : null}
    </Box>
  );
}

function DatePaneListItem(props: {
  occasion: WidgetWeatherDatePaneListItem_WeatherOccasion$key;
  isCompact?: boolean;
  relativeLabel?: boolean;
}) {
  const occasion = useFragment(
    graphql`
      fragment WidgetWeatherDatePaneListItem_WeatherOccasion on WidgetWeatherOccasion {
        date
        condition
        lowTemp
        highTemp
        ...WidgetWeatherConditionIcon_WeatherOccasion
      }
    `,
    props.occasion
  );
  const date = DateTime.fromISO(occasion.date);

  return (
    <Box
      style={{
        flexDirection: "row",
        height: props.isCompact ? "15%" : "20%",
        alignItems: "center",
        alignContent: "center",
        justifyContent: "space-between",
      }}
      spaceHorizontal="small"
    >
      <AHText
        style={{
          width: props.relativeLabel && props.isCompact ? "36%" : "25%",
          textAlign: props.relativeLabel && props.isCompact ? "left" : "center",
          ...textShadow,
        }}
        color="lightText"
        type="title-small"
      >
        {props.relativeLabel
          ? date.toRelativeCalendar({ unit: "days" })?.replace(" ", "")
          : date.toLocaleString({ weekday: "short" })}
      </AHText>
      <IconComponent occasion={occasion} />
      <Box
        style={{
          flexDirection: "row",
          justifyContent: "flex-end",
          flex: 1,
        }}
      >
        <AHText
          type="title-small"
          color="lightText"
          style={{
            flexBasis: props.relativeLabel && props.isCompact ? "35%" : "32%",
            textAlign: "center",
            opacity: 0.5,
          }}
        >
          {occasion.lowTemp}°
        </AHText>
        <AHText
          type="title-small"
          color="lightText"
          style={{
            flexBasis: "32%",
            textAlign: "center",
            ...textShadow,
          }}
        >
          {occasion.highTemp}°
        </AHText>
      </Box>
    </Box>
  );
}

export const WidgetWeather = React.memo(WidgetWeatherComponent);
